import React, { FC, useEffect, useMemo, useRef } from "react";
import { Chat, ChatMessageProps, ChatMessageSendEvent, User } from "@progress/kendo-react-conversational-ui";
import { makeStyles } from "@fluentui/react-components";
import { ChatBotMessageBoxContext, ChatBotMessageBoxWrapper } from "./ChatBotMessageBox";
import ChatBotMessageTemplate from "./ChatBotMessageTemplate";
import { ChatBotProps, TgkActionMessage, TgkLoadingMessage, TgkMessageModel, TgkTextMessage } from "./types";

const useStyles = makeStyles({
  chat: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    border: "none",
    height: "100vh",
    width: "100%",
    margin: "0px",
    overflow: "hidden",
  },
});

// Kendo passes the messageTemplate as `template` prop (not as children) to each `message`
// component, alongside `onRequestSelection` — a callback that triggers a timestamp
// toggle on click/focus, shifting messages below downward.
// By rendering `template` directly (bypassing ChatMessage) we fix the layout shift
// while still correctly displaying every message via ChatBotMessageTemplate.
interface ChatMessageInternalProps extends ChatMessageProps {
  template?: FC<{ item: TgkMessageModel }>;
}
const ChatBotChatMessage: FC<ChatMessageProps> = (props) => {
  const { item, template: Template } = props as ChatMessageInternalProps;
  if (!Template || !item) return null;
  return <Template item={item as TgkMessageModel} />;
};

export const bot = { id: 0 };
export const user = { id: 1 };

function newMsg(author: User, content: string, tplOverride?: () => React.ReactElement | null): TgkTextMessage {
  return { messageType: "message", author, html: content, tplOverride, selectionIndex: 0 };
}

export function newActionMsg(author: User, actionLabel: string, onAction: () => void): TgkActionMessage {
  return { messageType: "action", author, actionLabel, onAction, selectionIndex: 0 };
}

export function newLoadingMsg(): TgkLoadingMessage {
  return { messageType: "loading", author: bot, selectionIndex: 0 };
}

export function newBotMsg(content: string, tplOverride?: () => React.ReactElement | null): TgkTextMessage {
  return newMsg(bot, content, tplOverride);
}

export function newUserMsg(content: string, tplOverride?: () => React.ReactElement | null): TgkTextMessage {
  return newMsg(user, content, tplOverride);
}

export function newBotSystemMsg(content: string, tplOverride?: () => React.ReactElement | null): TgkTextMessage {
  return { ...newBotMsg(content, tplOverride), systemMessage: true };
}

export function newBotActionMsg(actionLabel: string, onAction: () => void): TgkActionMessage {
  return newActionMsg(bot, actionLabel, onAction);
}

export function newUserActionMsg(actionLabel: string, onAction: () => void): TgkActionMessage {
  return newActionMsg(user, actionLabel, onAction);
}

export function newUserActionLabel(actionLabel: string): TgkActionMessage {
  return { messageType: "action", author: user, actionLabel, actionStyle: "label", selectionIndex: 0 };
}

export const ChatBot: FC<ChatBotProps> = ({ placeholder, disclaimer, disabled, loading, messages, onMessageSend }) => {
  const classes = useStyles();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const forceScrollRef = useRef(false);

  // Shallow-copy each message to avoid mutating frozen Redux state objects.
  // Kendo Chat assigns `selectionIndex` directly on message objects; if they
  // are frozen (as Immer/Redux Toolkit produces) this throws a runtime error.
  const mutableMessages = messages.map((m) => ({ ...m }));
  const visibleMessages = loading ? [...mutableMessages, newLoadingMsg()] : mutableMessages;

  // Context value for the stable ChatBotMessageBoxWrapper
  const messageBoxContextValue = useMemo(
    () => ({
      disabled,
      placeholder,
      disclaimer,
      onMessageSend: (message: string) => {
        forceScrollRef.current = true;
        onMessageSend(newUserMsg(message));
      },
    }),
    [disabled, placeholder, disclaimer, onMessageSend]
  );

  // Attach scroll listener to track whether user is at bottom of message list
  useEffect(() => {
    const messageList = chatContainerRef.current?.querySelector(".k-message-list");
    if (!messageList) return;
    const handleScroll = () => {
      isAtBottomRef.current = messageList.scrollHeight - messageList.scrollTop - messageList.clientHeight < 30;
    };
    messageList.addEventListener("scroll", handleScroll);
    return () => messageList.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll when messages change (if at bottom or user just sent a message)
  useEffect(() => {
    if (!isAtBottomRef.current && !forceScrollRef.current) return;
    forceScrollRef.current = false;
    const messageList = chatContainerRef.current?.querySelector(".k-message-list");
    if (!messageList) return;
    requestAnimationFrame(() => {
      messageList.scrollTop = messageList.scrollHeight;
    });
  }, [messages.length, loading]);

  const addNewMessage = (event: ChatMessageSendEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { timestamp: _timestamp, ...message } = event.message;
    onMessageSend({ messageType: "message", ...message, selectionIndex: 1 });
  };

  return (
    <ChatBotMessageBoxContext.Provider value={messageBoxContextValue}>
      <div ref={chatContainerRef} className={classes.chat}>
        <Chat
          user={user}
          messages={visibleMessages}
          message={ChatBotChatMessage}
          messageBox={ChatBotMessageBoxWrapper}
          messageTemplate={ChatBotMessageTemplate}
          onMessageSend={addNewMessage}
        />
      </div>
    </ChatBotMessageBoxContext.Provider>
  );
};

export default ChatBot;
